import { lazy, type ComponentType } from 'react';

/**
 * lazy() that survives a deploy landing under a live tab.
 *
 * A browser holding a cached index.html from the previous deploy asks for a
 * hashed chunk that no longer exists. Vercel's SPA rewrite answers with
 * index.html, so the "module" is HTML — and what the component sees depends on
 * the engine. main.tsx already reloads once on Vite's `vite:preloadError`, which
 * covers the preloaded case, but it does NOT cover a dynamic import that
 * RESOLVES to something useless: Safari produced
 *
 *   TypeError: undefined is not an object (evaluating 'tr.DrawingCanvas')
 *
 * from `import('./DrawingCanvas').then((m) => ({ default: m.DrawingCanvas }))`
 * — the import settled, `m` was not a module, and no preloadError ever fired
 * (LIGHTMASTER-WEB-19, 2026-08-07, two users mid-deploy).
 *
 * So: verify we actually got a component, and treat "we did not" the same as a
 * failed import — reload once to pick up the fresh index.html. The sessionStorage
 * throttle is shared with main.tsx's handler so a genuinely broken deploy cannot
 * reload-loop the tab; past the throttle we rethrow and let the error boundary
 * show something honest rather than spinning.
 */
const RELOAD_KEY = 'lm-chunk-reload-at';
const RELOAD_THROTTLE_MS = 15_000;

/**
 * True once THIS page load has asked the browser to reload.
 *
 * In-memory on purpose, and the reason this module exists in the shape it does.
 * The sessionStorage stamp survives the reload — that is what stops a broken
 * deploy looping — but it cannot tell "a reload is already on its way" apart
 * from "we reloaded last time and it did not help". Those need opposite
 * behaviour, and conflating them is what kept LIGHTMASTER-WEB-19 reporting
 * after the first fix shipped:
 *
 *   1. Vite fires vite:preloadError, main.tsx stamps the key and calls reload().
 *   2. The reload has not navigated yet, so the dynamic import settles to HTML.
 *   3. `m.DrawingCanvas` throws; we look at the stamp, see it is seconds old,
 *      read that as "throttled — give up", and rethrow.
 *   4. The error boundary flashes an error on a page that is already leaving.
 *
 * Step 4 is exactly what the fallback component was added to prevent. With a
 * shared in-flight flag, whoever gets there second renders nothing instead.
 */
let reloadInFlight = false;

/**
 * Ask for one reload. Returns true if the caller should render nothing because
 * a reload is happening — whether or not this call is the one that started it.
 */
export function requestChunkReload(): boolean {
  if (reloadInFlight) return true;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last > RELOAD_THROTTLE_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      reloadInFlight = true;
      window.location.reload();
      return true;
    }
    // Stamped, but not by us this page load: the reload already happened and the
    // chunk is STILL broken. Let the error surface rather than hiding a genuinely
    // bad deploy behind a blank component.
  } catch {
    // Private mode / storage disabled — fall through and rethrow.
  }
  return false;
}

export function lazyWithReload<T extends ComponentType<unknown>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await load();
      if (!mod || typeof mod.default === 'undefined') {
        // Resolved, but not to a component — a stale chunk served as index.html.
        throw new Error('Lazy chunk resolved without a component export');
      }
      return mod;
    } catch (err) {
      if (requestChunkReload()) {
        // Reload is in flight; hand back a component that renders nothing so the
        // boundary does not flash an error during teardown.
        return { default: (() => null) as unknown as T };
      }
      throw err;
    }
  });
}
